import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva, cx } from "@/lib/style/cva.config";

const buttonVariants = cva({
  base: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-surface-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bordercolor-secondary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  variants: {
    variant: {
      default: "bg-button-primary text-txt-inverted hover:bg-button-primary/90",
      destructive: "bg-error-100 text-txt-inverted hover:bg-error-100/90",
      outline:
        "border border-bordercolor-primary bg-white hover:bg-neutral-50 hover:text-txt-primary",
      secondary:
        "bg-surface-secondary hover:bg-surface-secondary/80 text-txt-brand  font-bold text-md",
      ghost: "hover:bg-neutral-50 hover:text-txt-primary",
      link: "text-txt-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      xl: "h-14 rounded-md px-10",
      icon: "h-10 w-10",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    as?: "child" | "button";
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, as = "button", ...props },
  ref,
) {
  const Comp = as === "child" ? Slot : as;
  return <Comp className={cx(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
});

export { Button, buttonVariants };
